import { Geotab, Group, GroupFilterCondition } from "@fairfleet/geotab";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { uniqWith } from "lodash";
import { useAddInState } from "@/components/AddInStateProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import createUserGroupFilter from "@/utilities/createUserGroupFilter";

/**
 * Returns an array of users matching the given search string.
 *
 * @param search - The search string.
 * @returns - An array of {@link User}.
 */
export function useUsersBySearch(
  search: string,
  onlyDrivers: boolean,
  showArchived: boolean,
  me: boolean,
  enabled?: boolean,
) {
  const queryClient = useQueryClient();
  const { geotabClient, activeSessionId, clearGeotabClient } = useGeotabClient();
  const { groups, groupFilterCondition } = useAddInState();

  search = search?.trim();
  search = search?.replace("%", "");

  return useQuery({
    queryKey: [
      "usersBySearch",
      activeSessionId,
      search,
      onlyDrivers,
      showArchived,
      // Ignore groupfilter when searching me
      me ? undefined : groups,
      me ? undefined : groupFilterCondition,
      me,
    ] as const,

    queryFn: async ({ signal }) => {
      const users = await getUsers(
        geotabClient,
        search,
        onlyDrivers,
        showArchived,
        me ? undefined : groups,
        me ? undefined : groupFilterCondition,
        signal,
      )
        .then(user => uniqWith(user, (a, b) => a.id === b.id))
        .catch(e => {
          if (e instanceof Error && e.message.includes("Invalid session") && activeSessionId) {
            clearGeotabClient(activeSessionId);
            console.log("Session expired, clearing Geotab client");
            return [];
          }
        });

      for (const user of users ?? []) {
        queryClient.setQueryData(["user", activeSessionId, user.id], user);
      }

      return users ?? [];
    },
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function getUsers(
  geotabClient: Geotab,
  search: string,
  onlyDrivers: boolean,
  showArchived: boolean,
  groups?: Group[],
  groupFilterCondition?: GroupFilterCondition,
  signal?: AbortSignal,
) {
  const now_utc = new Date(new Date().toUTCString());

  // fromDate: now_utc, toDate: now_utc => only active not archived drivers
  if (search.length <= 2) {
    const filter = createUserGroupFilter(groups, groupFilterCondition, {
      isDriver: onlyDrivers,
      ...(!showArchived && { fromDate: now_utc }),
      ...(!showArchived && { toDate: now_utc }),
    });

    return geotabClient.get("User", filter, 100, signal) ?? [];
  }
  const filter = createUserGroupFilter(groups, groupFilterCondition, {
    isDriver: onlyDrivers,
    ...(!showArchived && { fromDate: now_utc }),
    ...(!showArchived && { toDate: now_utc }),
    keywords: search.split(" ").map(keyword => `%${keyword}%`),
  });

  return geotabClient.get("User", filter, 50, signal) ?? [];
}
